import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ExternalLink, Send } from 'lucide-react';
import { buttonClass, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';

/**
 * Telegramda tasdiqlash KUTILAYOTGAN payt ko'rinadigan panel.
 *
 * Nega alohida ekran, bir qator matn emas: odam bu paytda BOSHQA ilovada
 * turadi va brauzerga qaytganda "nima bo'lganini" darhol tushunishi kerak.
 * Panelda uchta narsa bor va uchalasi ham zarur:
 *
 *   — jonli nishon: kutish davom etayotgani ko'rinib turadi;
 *   — QADAMLAR: botda aynan nima bosilishini aytadi;
 *   — "Botni ochish" havolasi: yangi oynani brauzer TO'SIB QO'YISHI mumkin
 *     va o'shanda odam boshqa hech qanday yo'l topa olmasdi.
 */
export interface TelegramWaitPanelProps {
  deepLink: string;
  /**
   * Botda tanlanadigan RAQAM.
   *
   * Panelning eng katta elementi — va bu ataylab: odam telefonga qaraydi,
   * keyin ekranga qaytadi. Raqam kichkina bo'lsa, uni qidirib topish
   * kerak bo'lardi.
   */
  matchCode: string;
  /** ISO — shu vaqtdan keyin so'rov kuchini yo'qotadi. */
  expiresAt: string;
  onCancel: () => void;
}

const secondsLeft = (iso: string): number =>
  Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000));

const clock = (total: number): string => {
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const TelegramWaitPanel = ({
  deepLink,
  matchCode,
  expiresAt,
  onCancel,
}: TelegramWaitPanelProps) => {
  const reduceMotion = useReducedMotion() === true;
  const [left, setLeft] = useState(() => secondsLeft(expiresAt));

  useEffect(() => {
    const timer = setInterval(() => setLeft(secondsLeft(expiresAt)), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  return (
    <div
      className={cn(
        'border-border bg-surface-2 flex flex-col items-center gap-4',
        'rounded-[var(--radius-lg)] border px-5 py-6 text-center',
      )}
    >
      <div className="relative flex size-16 items-center justify-center">
        {/* Ikki halqa har xil kechikish bilan — bitta bo'lsa "yuklanmoqda"
            emas, "to'xtab qolgan" taassuroti qolardi. */}
        {reduceMotion
          ? null
          : [0, 0.6].map((delay) => (
              <motion.span
                key={delay}
                aria-hidden
                className="border-accent absolute inset-0 rounded-full border"
                initial={{ scale: 0.7, opacity: 0.55 }}
                animate={{ scale: 1.45, opacity: 0 }}
                transition={{ duration: 1.8, repeat: Infinity, delay, ease: 'easeOut' }}
              />
            ))}
        <span className="bg-accent text-accent-contrast relative flex size-12 items-center justify-center rounded-full">
          <Send size={20} aria-hidden />
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <p className="font-semibold tracking-tight">{t('login.telegramWaiting')}</p>
        <p className="text-text-muted text-sm text-pretty">{t('login.telegramWaitLead')}</p>
      </div>

      {/* Raqam — panelning MARKAZI. Botda aynan shu tanlanadi va bu
          hisobni o'g'irlashga qarshi yagona to'siq: begona havolani
          bosgan odam bu raqamni ko'rmaydi. */}
      <div className="flex w-full flex-col items-center gap-1.5">
        <span className="text-text-faint text-xs">{t('login.telegramMatchLabel')}</span>
        <span
          className={cn(
            'border-accent/30 bg-accent-soft text-accent tabular',
            'rounded-[var(--radius-lg)] border px-6 py-2 text-4xl font-semibold tracking-[0.2em]',
          )}
        >
          {matchCode}
        </span>
      </div>

      {/* Qadamlar RAQAMLANGAN, chunki ular haqiqiy ketma-ketlik: birinchisini
          bajarmasdan ikkinchisi ko'rinmaydi. */}
      <ol className="text-text-muted flex w-full flex-col gap-2 text-left text-sm">
        {['one', 'two'].map((step, index) => (
          <li key={step} className="flex items-start gap-2.5">
            <span
              aria-hidden
              className="bg-accent-soft text-accent tabular mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
            >
              {index + 1}
            </span>
            <span className="text-pretty">{t(`login.telegramStep.${step}`)}</span>
          </li>
        ))}
      </ol>

      <div className="flex w-full flex-col gap-2">
        {/* Oyna to'silgan bo'lsa YAGONA yo'l shu havola. `noopener` — havola
            tashqi (t.me) va u bu oynani boshqara olmasligi kerak. */}
        <a
          href={deepLink}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass({ variant: 'secondary', className: 'w-full' })}
        >
          <ExternalLink size={15} aria-hidden />
          {t('login.telegramOpen')}
        </a>

        <div className="text-text-faint flex items-center justify-between text-xs">
          <button
            type="button"
            onClick={onCancel}
            className="hover:text-text transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)]"
          >
            {t('login.telegramCancel')}
          </button>
          <span className="tabular">{t('login.telegramLeft', { time: clock(left) })}</span>
        </div>
      </div>
    </div>
  );
};
